import CloseIcon from '@mui/icons-material/Close';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, Chip, Divider, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Confirm, Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { deliverMovesToDistributor } from '../../data/api/incomes/IncomeRoutes';
import useIndex from '../../hooks/useIndex';
import validErrors from '../../hooks/validErrors';

/**
 * Modal para que el admin entregue jugadas a un Distribuidor.
 *
 * A diferencia del regalo a un jugador, distingue dos cantidades:
 *  - moves: jugadas entregadas (se facturan: el backend genera un invoice).
 *  - gift_moves: jugadas de regalo (bonus, no se facturan).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - targetUser: { uuid, name, lastname, moves } — distribuidor receptor
 *  - onSuccess: callback opcional con el payload del backend
 */
const DeliverMovesModal = ({ open, onClose, targetUser, onSuccess }) => {
    const { token } = useIndex();
    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({ defaultValues: { moves: '', gift_moves: '' } });

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) reset({ moves: '', gift_moves: '' });
    }, [open]);

    const watchedMoves = Number(watch('moves')) || 0;
    const watchedGift = Number(watch('gift_moves')) || 0;

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose?.();
    };

    const numberFieldProps = {
        /* Bloquea negativos / notación científica y evita que el scroll del
           mouse cambie el valor. */
        onWheel: (e) => e.target.blur(),
        onKeyDown: (e) => {
            if (['-', 'e', 'E', '+', '.', ','].includes(e.key)) {
                e.preventDefault();
            }
        },
    };

    const sendDelivery = async (moves, giftMoves) => {
        setSubmitting(true);
        try {
            const { data } = await deliverMovesToDistributor(token, {
                user_uuid: targetUser?.uuid,
                moves,
                gift_moves: giftMoves,
            });
            if (data?.success) {
                Report.success(
                    'Entrega realizada',
                    data?.message ?? 'Jugadas entregadas correctamente.',
                    'Aceptar',
                    { textAlign: 'center' },
                );
                onSuccess?.(data?.data);
                onClose?.();
                reset();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (form) => {
        const moves = Number(form.moves) || 0;
        const giftMoves = Number(form.gift_moves) || 0;

        if (!Number.isInteger(moves) || !Number.isInteger(giftMoves) || moves < 0 || giftMoves < 0) {
            return;
        }
        if (moves + giftMoves < 1) {
            Report.warning('Atención', 'Indica al menos una jugada (entregada o de regalo).', 'Okay');
            return;
        }

        Confirm.show(
            'Confirmar entrega',
            `¿Entregar ${moves} jugada(s)${giftMoves > 0 ? ` y ${giftMoves} de regalo` : ''} a ${targetUser?.name ?? ''} ${targetUser?.lastname ?? ''}? Se generará una factura por las jugadas entregadas.`,
            'Sí, entregar',
            'Cancelar',
            () => sendDelivery(moves, giftMoves),
            () => { },
        );
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Center>
                <Card sx={{ width: '100%', maxWidth: 480 }}>
                    <CardContent>
                        <Header>
                            <Box className="title-wrap">
                                <Box className="icon-box">
                                    <LocalShippingOutlinedIcon />
                                </Box>
                                <Typography variant="h6">Entregar jugadas</Typography>
                            </Box>
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Distribuidor
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: '#10285f', fontWeight: 900 }}>
                                {targetUser?.name} {targetUser?.lastname ?? ''}
                            </Typography>
                            <Chip
                                size="small"
                                label={`${Number(targetUser?.moves ?? 0)} jugadas actuales`}
                                sx={{ mt: 0.5 }}
                            />
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Jugadas a entregar"
                                inputProps={{ min: 0, step: 1 }}
                                {...numberFieldProps}
                                error={!!errors.moves}
                                helperText={errors.moves?.message ?? 'Estas jugadas se facturan.'}
                                {...register('moves', {
                                    valueAsNumber: true,
                                    min: { value: 0, message: 'No puede ser negativo.' },
                                    validate: (v) => Number.isInteger(Number(v || 0)) || 'Debe ser un número entero.',
                                })}
                            />

                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Jugadas de regalo"
                                inputProps={{ min: 0, step: 1 }}
                                sx={{ mt: 2 }}
                                {...numberFieldProps}
                                error={!!errors.gift_moves}
                                helperText={errors.gift_moves?.message ?? 'Bonus, no se facturan.'}
                                {...register('gift_moves', {
                                    valueAsNumber: true,
                                    min: { value: 0, message: 'No puede ser negativo.' },
                                    validate: (v) => Number.isInteger(Number(v || 0)) || 'Debe ser un número entero.',
                                })}
                            />

                            <SummaryBox>
                                <Box className="row">
                                    <span><LocalShippingOutlinedIcon fontSize="inherit" /> Entregadas</span>
                                    <strong>{watchedMoves}</strong>
                                </Box>
                                <Box className="row">
                                    <span><RedeemOutlinedIcon fontSize="inherit" /> De regalo</span>
                                    <strong>{watchedGift}</strong>
                                </Box>
                                <Divider sx={{ my: 0.8 }} />
                                <Box className="row total">
                                    <span>Total a acreditar</span>
                                    <strong>{watchedMoves + watchedGift}</strong>
                                </Box>
                            </SummaryBox>

                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                startIcon={<LocalShippingOutlinedIcon />}
                                sx={{
                                    mt: 2,
                                    borderRadius: '12px',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #e67e22 0%, #d35400 100%)',
                                    boxShadow: '0 6px 14px rgba(214, 124, 34, 0.28)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #d35400 0%, #e67e22 100%)',
                                    },
                                }}
                            >
                                <span>Entregar</span>
                            </LoadingButton>
                        </form>
                    </CardContent>
                </Card>
            </Center>
        </Modal>
    );
};

export default DeliverMovesModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 480px;
    outline: none;
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(214, 124, 34, 0.16);
        border: 1px solid rgba(214, 124, 34, 0.32);
        color: #d35400;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const SummaryBox = styled(Box)`
    margin-top: 1.2rem;
    padding: 0.8rem 1rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.04);
    border: 1px solid rgba(15, 34, 77, 0.1);

    .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.85rem;
        font-weight: 700;
        color: rgba(15, 34, 77, 0.72);
        padding: 2px 0;

        span {
            display: flex;
            align-items: center;
            gap: 6px;
        }

        strong {
            color: #10285f;
            font-weight: 900;
        }

        &.total {
            font-size: 0.95rem;
            color: #10285f;
        }
    }
`;
